import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth as useClerkAuth } from "@clerk/clerk-react";
import type { User } from "@shared/models/auth";
import {
  getClerkSessionResolution,
  isAuthStateLoading,
} from "@/lib/authSessionState";

async function fetchUser(): Promise<User | null> {
  const response = await fetch("/api/auth/user", {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    // Don't throw on server errors — return null so the UI degrades
    // gracefully (shows "Sign In" rather than crashing or getting stuck).
    // The auth invalidation effect will re-fetch when Clerk state changes.
    console.warn(`[auth] /api/auth/user returned ${response.status} — treating as unauthenticated`);
    return null;
  }

  return response.json();
}

const CLERK_LOAD_TIMEOUT_MS = 5000;

export function useAuth() {
  const queryClient = useQueryClient();
  const { isSignedIn, isLoaded: clerkLoaded, signOut } = useClerkAuth();

  // A timeout is diagnostic only. Never convert a slow or blocked Clerk load
  // into a signed-out state: doing so makes a valid persistent session appear
  // expired and sends returning mobile/PWA users back through email OTP.
  const [clerkTimedOut, setClerkTimedOut] = useState(false);
  useEffect(() => {
    if (clerkLoaded) return;
    const t = setTimeout(() => {
      console.warn('[auth] Clerk session is still loading after 5 seconds', {
        state: 'loading',
        origin: window.location.origin,
        displayMode: window.matchMedia('(display-mode: standalone)').matches
          ? 'standalone'
          : 'browser',
      });
      setClerkTimedOut(true);
    }, CLERK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [clerkLoaded]);

  useEffect(() => {
    if (!clerkLoaded) return;
    console.info('[auth] Clerk session resolved', {
      state: getClerkSessionResolution(clerkLoaded, isSignedIn),
      origin: window.location.origin,
      displayMode: window.matchMedia('(display-mode: standalone)').matches
        ? 'standalone'
        : 'browser',
    });
  }, [clerkLoaded, isSignedIn]);

  // When Clerk's sign-in state changes, invalidate the cached user so the
  // app immediately reflects the new session instead of showing stale data.
  useEffect(() => {
    if (clerkLoaded) {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    }
  }, [isSignedIn, clerkLoaded]);

  const { data: user, isLoading: queryLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
    // Wait for Clerk to finish restoring its browser session before asking the
    // server for the corresponding app user. This prevents a pre-hydration 401
    // from being cached and rendered as a sign-in requirement.
    enabled: clerkLoaded,
  });

  // isLoading remains true until Clerk definitively resolves the browser
  // session. clerkTimedOut is deliberately not part of this calculation.
  // Both Clerk and the app-user query must settle before rendering auth UI:
  //   • queryLoading alone: Clerk loaded but /api/auth/user still in flight —
  //     the old formula returned false here, letting Scorecard boot with
  //     user=undefined and isAuthenticated=false before the query settled.
  //   • !clerkLoaded: Clerk is still restoring or refreshing the session.
  const isLoading = isAuthStateLoading(clerkLoaded, queryLoading);

  useEffect(() => {
    if (!clerkLoaded || queryLoading) return;
    if (isSignedIn && user === null) {
      console.warn('[auth] Clerk is signed in but the app user session is unavailable', {
        clerkState: 'signed_in',
        appUserState: 'unavailable',
      });
    }
  }, [clerkLoaded, isSignedIn, queryLoading, user]);

  const logout = async () => {
    try {
      await fetch("/api/logout", { credentials: "include" });
    } catch {}
    if (isSignedIn) {
      await signOut();
    }
    queryClient.setQueryData(["/api/auth/user"], null);
  };

  // ── DEV-only test hooks ─────────────────────────────────────────────────────
  // window.__dpTestForceAuthFalse = true  → simulates isAuthenticated = false
  //   while Clerk session is still valid (transient server fetch failure).
  // window.__dpTestClerkSignedOut = true  → additionally makes isClerkSignedIn
  //   false (simulates confirmed session loss / revocation).
  // Never compiled into production builds.
  const testForceAuthFalse =
    import.meta.env.DEV && (window as any).__dpTestForceAuthFalse === true;
  const testForceClerkSignedOut =
    import.meta.env.DEV && (window as any).__dpTestClerkSignedOut === true;
  // ── End test hooks ──────────────────────────────────────────────────────────

  return {
    user: testForceAuthFalse ? null : user,
    isLoading,
    isAuthenticated: testForceAuthFalse ? false : !!user,
    // Clerk signal granularity — used to distinguish transient failures from
    // confirmed session loss, and to drive passive vs escalated auth messaging.
    isClerkSignedIn: testForceClerkSignedOut ? false : (isSignedIn ?? false),
    isClerkLoaded: clerkLoaded,
    clerkTimedOut,
    logout,
    isLoggingOut: false,
  };
}
